import { create } from 'zustand'
import { fetchExportsHistory, startExport } from '@api/exports'
import {
  EXPORT_FORMAT_EMAILS_ONLY,
  EXPORT_STATUS_DONE,
  GROUPING_ALL_IN_ONE,
  PLAYER_FILTER_ALL,
} from '@constants/exports'
import {
  type AvailableExportGame,
  type AvailableExportGameApiItem,
  type ExportHistoryApiItem,
} from '@app-types/api/exports'
import { type ExportJob, type ExportState } from '@app-types/stores/exports'

const dateFormatter = new Intl.DateTimeFormat('ru-RU', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
})

function normalizeGame(item: AvailableExportGameApiItem): AvailableExportGame {
  if (typeof item === 'string') {
    return { slug: item, title: item }
  }

  return {
    slug: item.slug,
    title: item.title || item.slug,
  }
}

function toJob(item: ExportHistoryApiItem): ExportJob {
  const createdAt = item.created_at ? new Date(item.created_at) : null
  const fileReady = item.status === EXPORT_STATUS_DONE && Boolean(item.file_name)

  return {
    id: item.id,
    exportedAt: createdAt ? dateFormatter.format(createdAt) : '-',
    games: item.games,
    playerFilter: item.player_filter,
    exportFormat: item.export_format,
    grouping: item.grouping,
    status: item.status,
    fileUrl: fileReady ? `/api/exports/download/${item.id}/` : null,
    fileName: item.file_name || '',
    archivePassword: item.archive_password || '',
  }
}

export const useExportStore = create<ExportState>((set) => ({
  availableGames: [],
  error: '',
  filters: {
    games: [],
    playerFilter: PLAYER_FILTER_ALL,
    exportFormat: EXPORT_FORMAT_EMAILS_ONLY,
    grouping: GROUPING_ALL_IN_ONE,
  },
  isHistoryLoading: false,
  isStarting: false,
  jobs: [],

  loadInitialData: async () => {
    set({ error: '', isHistoryLoading: true })

    try {
      const payload = await fetchExportsHistory()
      set({
        availableGames: (payload.available_games ?? []).map(normalizeGame),
        jobs: (payload.items ?? []).map(toJob),
      })
    } catch (error) {
      set({
        error:
          error instanceof Error
            ? error.message
            : 'Не удалось загрузить выгрузки',
      })
    } finally {
      set({ isHistoryLoading: false })
    }
  },

  setFilters: (filters) => {
    set({ filters })
  },

  startExport: async (values, csrfToken) => {
    set({ error: '', isStarting: true })

    try {
      const payload = await startExport(values, csrfToken)
      set((state) => ({
        jobs: [toJob(payload.item), ...state.jobs],
      }))
    } catch (error) {
      set({
        error:
          error instanceof Error
            ? error.message
            : 'Не удалось запустить выгрузку',
      })
      throw error
    } finally {
      set({ isStarting: false })
    }
  },
}))
